import React, { useState, useEffect, useRef } from 'react';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { CheckCircle, Loader2, Search } from 'lucide-react';
import { apiService } from '../../services/apiService';
import { addIconsToServices } from '../../utils/serviceIcons';

export const ProjectBasicsStep = ({ formData, updateFormData, validationErrors = {} }) => {
  const [availableServices, setAvailableServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const detailsRef = useRef(null);

  // Fetch services from API on mount
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoadingServices(true);
        const data = await apiService.get('/api/v1/services');
        const servicesWithIcons = addIconsToServices(data);
        setAvailableServices(servicesWithIcons);
      } catch (error) {
        console.error('Failed to fetch services:', error);
        setAvailableServices([]);
      } finally {
        setLoadingServices(false);
      }
    };
    fetchServices();
  }, []);

  const handleInputChange = (field, value) => {
    updateFormData({ [field]: value });
  };

  const handleCategorySelect = (serviceId) => {
    updateFormData({ service_id: serviceId });
    setTimeout(() => {
      detailsRef.current?.focus({ preventScroll: true });
      detailsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  };

  return (
    <div className="space-y-8 mb-12">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Tell Us About Your Project
        </h3>
        <p className="text-gray-600">
          Start by selecting your service category, then provide details about your project
        </p>
      </div>

      {/* Service Category - FIRST */}
      <div className="space-y-4">
        <Label className="text-base font-medium text-gray-900">
          What service do you need? *
        </Label>
        {validationErrors.service_id && (
          <p className="text-sm text-red-600 font-medium">{validationErrors.service_id}</p>
        )}
        {loadingServices ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 text-emerald-600 animate-spin mr-2" />
            <span className="text-gray-600">Loading services...</span>
          </div>
        ) : availableServices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No services available. Please try again later.
          </div>
        ) : (
        <>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="text"
              placeholder="Search services (e.g. gardening, mulch, painting...)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-10 border-2 focus:border-emerald-500 transition-colors duration-200"
            />
          </div>
        <div className="max-h-[420px] overflow-y-auto border border-gray-200 rounded-lg p-3">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableServices.filter(s =>
            !searchQuery ||
            s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (s.description || '').toLowerCase().includes(searchQuery.toLowerCase())
          ).length === 0 ? (
            <div className="col-span-full text-center py-8 text-gray-500">
              No services match your search. Try a different keyword.
            </div>
          ) : availableServices.filter(s =>
            !searchQuery ||
            s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (s.description || '').toLowerCase().includes(searchQuery.toLowerCase())
          ).map((service) => (
            <Card
              key={service.id}
              className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                formData.service_id == service.id
                  ? 'ring-2 ring-emerald-500 bg-emerald-50 border-emerald-200'
                  : 'hover:border-gray-300'
              }`}
              onClick={() => handleCategorySelect(service.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${
                    formData.service_id == service.id
                      ? 'bg-emerald-100'
                      : 'bg-gray-100'
                  }`}>
                    <service.icon className={`w-5 h-5 ${
                      formData.service_id == service.id
                        ? 'text-emerald-600'
                        : 'text-gray-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      {service.name}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {service.description}
                    </p>
                    {formData.service_id == service.id && (
                      <div className="mt-2">
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                          Selected
                        </Badge>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          </div>
        </div>
        </>
        )}
      </div>

      {/* Project Description - SECOND */}
      <div className="space-y-2">
        <Label htmlFor="details" className="text-base font-medium text-gray-900">
          Describe your project *
        </Label>
        {validationErrors.details && (
          <p className="text-sm text-red-600 font-medium">{validationErrors.details}</p>
        )}
        <Textarea
          id="details"
          ref={detailsRef}
          placeholder="Describe what work needs to be done, any specific requirements, challenges, or preferences you have..."
          value={formData.details}
          onChange={(e) => handleInputChange('details', e.target.value)}
          className={`min-h-[120px] text-base border-2 focus:border-emerald-500 transition-colors duration-200 resize-none ${
            validationErrors.details ? 'border-red-500 focus:border-red-500' : ''
          }`}
        />
        <p className="text-sm text-gray-500">
          The more details you provide, the better quotes you'll receive
        </p>
      </div>

      {/* Progress Indicator */}
      <div className="text-center pt-2 pb-8">
        {formData.service_id && formData.details.trim().length >= 10 ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <div className="flex items-center justify-center space-x-2 text-emerald-700">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">Step 1 Complete! Ready to continue.</span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            Please complete all required fields to continue
          </p>
        )}
      </div>
    </div>
  );
};